import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AgentContentBlock, AgentMessage, parseToolResult } from "./types";
import ChartCard from "./ChartCard";

// Compact, chat-friendly element styles. We keep margins tight so consecutive
// markdown elements don't push the bubble taller than necessary.
const md: Record<string, React.CSSProperties> = {
  p: { margin: "0 0 8px 0" },
  pLast: { margin: 0 },
  ul: { margin: "4px 0 8px 0", paddingLeft: 20 },
  ol: { margin: "4px 0 8px 0", paddingLeft: 20 },
  li: { marginBottom: 2 },
  code: {
    background: "rgba(15,23,42,0.06)",
    padding: "1px 5px",
    borderRadius: 4,
    fontSize: 12.5,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  pre: {
    background: "rgba(15,23,42,0.06)",
    padding: 10,
    borderRadius: 8,
    fontSize: 12.5,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    overflowX: "auto",
    margin: "4px 0 8px 0",
  },
  blockquote: {
    borderLeft: "3px solid #cbd5e1",
    paddingLeft: 10,
    color: "#475569",
    margin: "4px 0 8px 0",
  },
  a: { color: "#3949ab", textDecoration: "underline" },
  table: {
    borderCollapse: "collapse",
    margin: "4px 0 8px 0",
    fontSize: 12.5,
  },
  th: {
    borderBottom: "1px solid #cbd5e1",
    textAlign: "left",
    padding: "4px 8px",
    fontWeight: 600,
  },
  td: {
    borderBottom: "1px solid #e5e7eb",
    padding: "4px 8px",
  },
  hr: { border: "none", borderTop: "1px solid #e5e7eb", margin: "10px 0" },
};

const Markdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      // Render markdown into our chat-tuned styles. We disable raw HTML by
      // omitting rehype-raw, so any `<script>` etc. in agent output is escaped.
      p: ({ node, ...props }) => <p style={md.p} {...props} />,
      ul: ({ node, ...props }) => <ul style={md.ul} {...props} />,
      ol: ({ node, ...props }) => <ol style={md.ol} {...props} />,
      li: ({ node, ...props }) => <li style={md.li} {...props} />,
      code: ({ node, inline, ...props }: any) =>
        inline ? (
          <code style={md.code} {...props} />
        ) : (
          <code {...props} />
        ),
      pre: ({ node, ...props }) => <pre style={md.pre} {...props} />,
      blockquote: ({ node, ...props }) => <blockquote style={md.blockquote} {...props} />,
      a: ({ node, ...props }) => (
        <a {...props} target="_blank" rel="noreferrer" style={md.a} />
      ),
      table: ({ node, ...props }) => <table style={md.table} {...props} />,
      th: ({ node, ...props }) => <th style={md.th} {...props} />,
      td: ({ node, ...props }) => <td style={md.td} {...props} />,
      hr: ({ node, ...props }) => <hr style={md.hr} {...props} />,
    }}
  >
    {children}
  </ReactMarkdown>
);

interface Props {
  message: AgentMessage;
}

const userBubble: React.CSSProperties = {
  alignSelf: "flex-end",
  background: "#3949ab",
  color: "#fff",
  borderRadius: "14px 14px 4px 14px",
  padding: "10px 14px",
  maxWidth: "78%",
  fontSize: 14,
  lineHeight: 1.45,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const assistantWrap: React.CSSProperties = {
  alignSelf: "flex-start",
  maxWidth: "92%",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const assistantText: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#111827",
  borderRadius: "14px 14px 14px 4px",
  padding: "10px 14px",
  fontSize: 14,
  lineHeight: 1.5,
  wordBreak: "break-word",
};

const systemBubble: React.CSSProperties = {
  alignSelf: "stretch",
  background: "#fef2f2",
  color: "#7f1d1d",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 13,
  border: "1px solid #fecaca",
};

// ---------- tool-call chips ----------

const chipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: 11.5,
  fontWeight: 500,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const chipStyles: Record<string, React.CSSProperties> = {
  running: { background: "#eef2ff", color: "#3730a3", borderColor: "#c7d2fe" },
  ok: { background: "#f0fdf4", color: "#15803d", borderColor: "#bbf7d0" },
  error: { background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" },
};

function chipSummaryFor(toolName: string, parsed: ReturnType<typeof parseToolResult>): string {
  if (parsed.kind === "chart") return "chart";
  if (parsed.kind === "error") return parsed.error || "error";
  // parsed.kind === "json" - look at the shape
  const obj = (parsed.json as Record<string, unknown>) || {};
  if (toolName === "render_chart") return "chart";
  if (typeof obj.row_count === "number") return `${obj.row_count} rows`;
  const stats = obj.stats as Record<string, unknown> | undefined;
  if (stats && typeof stats.total_sum === "number") return `${stats.total_sum} events`;
  if (stats && typeof stats.count === "number") return `${stats.count} rows`;
  if (Array.isArray(obj.rows)) return `${obj.rows.length} rows`;
  return "done";
}

const RunningSpinner: React.FC = () => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      border: "1.5px solid currentColor",
      borderTopColor: "transparent",
      borderRadius: "50%",
      animation: "ai-chip-spin 0.7s linear infinite",
    }}
  />
);

interface ToolChipProps {
  toolName: string;
  // The matching tool_result block, if any. Absent = still in flight.
  resultContent?: string;
}

const ToolChip: React.FC<ToolChipProps> = ({ toolName, resultContent }) => {
  if (resultContent == null) {
    return (
      <span style={{ ...chipBase, ...chipStyles.running }}>
        <RunningSpinner />
        {toolName}
      </span>
    );
  }
  const parsed = parseToolResult(resultContent);
  const isError = parsed.kind === "error";
  const summary = chipSummaryFor(toolName, parsed);
  return (
    <span style={{ ...chipBase, ...(isError ? chipStyles.error : chipStyles.ok) }}>
      <span aria-hidden="true">{isError ? "✗" : "✓"}</span>
      <span>{toolName}</span>
      <span style={{ opacity: 0.7 }}>· {summary}</span>
    </span>
  );
};

const chipRow: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

function renderAssistantBlocks(blocks: AgentContentBlock[]): React.ReactNode[] {
  // Tool chips need to combine tool_use (the name) with tool_result (the
  // outcome). We index results by tool_use_id, then render in source order
  // so chips appear inline between text blocks the way the model emitted them.
  const resultsById: Record<string, string> = {};
  for (const b of blocks) {
    if (b.type === "tool_result") {
      resultsById[b.tool_use_id] = b.content;
    }
  }

  const out: React.ReactNode[] = [];
  // Coalesce adjacent tool_use blocks into one chip row so a model that fires
  // many tools in a single round doesn't render as a stack of separate rows.
  let pendingChips: React.ReactNode[] = [];
  const flushChips = (key: string) => {
    if (pendingChips.length === 0) return;
    out.push(
      <div key={`chips-${key}`} style={chipRow}>
        {pendingChips}
      </div>
    );
    pendingChips = [];
  };

  blocks.forEach((b, i) => {
    if (b.type === "text") {
      flushChips(`pre-${i}`);
      const text = b.text.trim();
      if (!text) return;
      out.push(
        <div key={`t-${i}`} style={assistantText}>
          <Markdown>{text}</Markdown>
        </div>
      );
    } else if (b.type === "tool_use") {
      pendingChips.push(
        <ToolChip
          key={`chip-${b.id}`}
          toolName={b.name}
          resultContent={resultsById[b.id]}
        />
      );
    } else if (b.type === "tool_result") {
      // The chip already covers this block. We additionally render any chart
      // payloads as a full ChartCard below the chip row.
      const parsed = parseToolResult(b.content);
      if (parsed.kind === "chart" && parsed.chart) {
        flushChips(`pre-chart-${i}`);
        out.push(<ChartCard key={`c-${i}`} spec={parsed.chart} />);
      }
    }
  });
  flushChips("end");
  return out;
}

const ChatMessage: React.FC<Props> = ({ message }) => {
  if (message.role === "user") {
    return <div style={userBubble}>{message.content as string}</div>;
  }
  if (message.role === "system") {
    return <div style={systemBubble}>{message.content as string}</div>;
  }
  const blocks = Array.isArray(message.content) ? message.content : [];
  const rendered = renderAssistantBlocks(blocks);
  // Empty assistant messages (the in-progress slot before the first delta
  // arrives, or a rare turn that produced nothing) render nothing - the
  // separate thinking indicator in ChatPanel already covers the waiting UX.
  if (rendered.length === 0) return null;
  return <div style={assistantWrap}>{rendered}</div>;
};

export default ChatMessage;
